import React from 'react';
import { Link } from 'react-router-dom';
import { Apple, BookOpen, Leaf, Heart, BookMarked, GlassWater, UtensilsCrossed, ShieldCheck, Beaker, ArrowRight } from 'lucide-react';
import { useContentGate, GateModal } from '../components/ContentGateWrapper';

const IntegrativeHealthPage = () => {
  const { isGated, hasAccess } = useContentGate();
  
  const subsections = [
    {
      path: '/integrative-health/food-nutrition',
      title: 'Food & Nutrition',
      description: 'Educational information about whole foods, dietary patterns, and nutritional principles commonly discussed in integrative oncology and patient communities.',
      icon: Apple
    },
    {
      path: '/integrative-health/recipes',
      title: 'Recipes (Supportive)',
      description: 'Simple, whole-food recipes often chosen by people seeking plant-forward, anti-inflammatory eating patterns.',
      icon: UtensilsCrossed
    },
    {
      path: '/integrative-health/smoothies',
      title: 'Smoothies',
      description: 'Educational information about smoothies for nutritional support when appetite is low or eating feels challenging.',
      icon: GlassWater
    },
    {
      path: '/integrative-health/eating-well',
      title: 'Eating Well With CLL',
      description: 'Supportive nutrition guidance covering practical approaches like eating enough, food variety, and fatigue-friendly eating.',
      icon: Apple
    },
    {
      path: '/integrative-health/food-safety',
      title: 'Food Safety',
      description: 'Important food safety information including raw food risks, temperature safety, and immunocompromised considerations.',
      icon: ShieldCheck,
      isPublic: true
    },
    {
      path: '/integrative-health/supplements',
      title: 'Supplements (Educational)',
      description: 'Non-prescriptive information about vitamins, minerals, and supplements that are sometimes discussed in CLL communities.',
      icon: Leaf
    },
    {
      path: '/integrative-health/lifestyle',
      title: 'Lifestyle & Habits',
      description: 'Movement, sleep, stress management, and daily habits that may support general wellbeing.',
      icon: Heart
    },
    {
      path: '/integrative-health/learn-notes',
      title: 'Learn & Notes',
      description: 'A personal space to record learnings, reflections, and questions as you explore Integrative Health content.',
      icon: BookMarked
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header - Always visible */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Integrative Health Support for People Living with CLL
        </h1>
        
        {/* Introduction Section - Always visible */}
        <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            How to use this section
          </h2>
          
          <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              Many people living with CLL explore topics like nutrition, lifestyle, and supplements alongside their standard medical care. This section provides educational context for those conversations — not plans, protocols, or recommendations.
            </p>
            
            <p>
              The information here is intended to help you understand what these topics mean, why they&apos;re sometimes discussed, and what questions you might want to bring to your healthcare team. It&apos;s not medical advice, and it doesn&apos;t replace clinical guidance.
            </p>
            
            <p>
              Some people find this content helpful when thinking about their overall wellbeing. Others prefer to focus only on clinical care. Both approaches are valid. Use what feels relevant to you, and skip what doesn&apos;t.
            </p>
            
            <p className="font-medium text-slate-900 pt-2 border-t border-slate-200">
              If something here interests you, we encourage you to discuss it with your doctor, oncologist, or dietitian. They can help you understand what might or might not be appropriate for your specific situation.
            </p>
          </div>
        </div>
      </div>

      {/* Featured Research Highlight */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Beaker className="h-5 w-5 text-green-600" />
          Featured CLL Research
        </h2>
        <Link
          to="/green-tea-research"
          className="block bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border border-green-200 rounded-2xl p-6 hover:shadow-lg hover:border-green-300 transition-all group"
          data-testid="featured-research-link"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
              <Beaker className="h-8 w-8 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  Mayo Clinic Study
                </span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 group-hover:text-green-700 transition-colors mb-2">
                Green Tea Extract (EGCG) & CLL
              </h3>
              <p className="text-slate-600 mb-3">
                Phase II clinical trial showed 69% of early-stage CLL patients had a biological response to EGCG, with many seeing reductions in lymphocyte counts and lymph node size.
              </p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-700 font-semibold">69% response rate</span>
                <span className="text-slate-400">•</span>
                <span className="text-slate-600">42 patients studied</span>
                <span className="text-slate-400">•</span>
                <span className="text-slate-600">Low toxicity</span>
              </div>
            </div>
            <ArrowRight className="h-6 w-6 text-green-500 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>

      {/* Subsections Grid - Titles and descriptions always visible, gated on click for free users */}
      <div className="grid md:grid-cols-2 gap-6">
        {subsections.map((section) => {
          const Icon = section.icon;
          
          // For free users, show card but gate navigation
          if (isGated) {
            return (
              <SubsectionCardGated
                key={section.path}
                section={section}
                Icon={Icon}
              />
            );
          }
          
          // For paid/admin users, show normal link
          return (
            <Link
              key={section.path}
              to={section.path}
              className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all hover:border-slate-200"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Icon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {section.description}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Gated subsection card for free users
 * Shows preview of content, but reveals upgrade prompt when clicked
 * Uses portal-based modal to avoid clipping issues
 */
const SubsectionCardGated = ({ section, Icon }) => {
  const [showGate, setShowGate] = React.useState(false);

  return (
    <>
      <div
        onClick={() => setShowGate(true)}
        className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all hover:border-slate-200 cursor-pointer"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-xl">
            <Icon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {section.title}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {section.description}
            </p>
          </div>
        </div>
      </div>
      
      {/* Portal-based modal - renders at document.body level */}
      <GateModal
        isOpen={showGate}
        onClose={() => setShowGate(false)}
        title={`Unlock ${section.title}`}
        compact
      />
    </>
  );
};

export default IntegrativeHealthPage;
